import type { ConfiguratorPreviewContext } from '../preview-context';

interface TokenEndpointResponse {
    token: string;
    expires_in?: number;
}

export class TokenResolutionError extends Error {
    constructor(
        public readonly status: number,
        public readonly bodySnippet: string,
    ) {
        super(`Token resolution failed (HTTP ${status})`);
        this.name = 'TokenResolutionError';
    }
}

export interface ResolveTokenOptions {
    /**
     * Bypass the plugin-side mint cache. The Magento token controller caches
     * each admin-mint for 8 minutes for performance, so a normal retry would
     * hand back the SAME (just-rejected) token. On the 401-retry path we
     * append `force=1` to the endpoint URL so the plugin skips its cache
     * and re-mints from admin.
     */
    forceRefresh?: boolean;
}

/**
 * Resolve the `x-jscloud-tok` to use for cloud-frontend calls.
 *
 *  - `ctx.tokenEndpoint` set → POST to the shop-side proxy URL, parse
 *    `{token, expires_in}`. Plugin-storefront path. Wins over an embedded
 *    `token` so a stale value in old cached HTML can't silently take over.
 *  - Otherwise → return `ctx.token` (admin-iframe preview path, token was
 *    minted server-side at render time and embedded directly).
 *
 * Throws {@link TokenResolutionError} when the endpoint is configured but
 * unreachable or returns a malformed body. Callers translate that into the
 * appropriate per-call error type (`ComponentFetchError`, etc.).
 */
export async function resolveToken(
    ctx: ConfiguratorPreviewContext,
    opts: ResolveTokenOptions = {},
): Promise<string> {
    if (ctx.tokenEndpoint && ctx.tokenEndpoint.length > 0) {
        // Keyed by the URL actually asked: two Components on one page that
        // share an endpoint without an id get a token each.
        const endpoint = withComponentId(ctx.tokenEndpoint, String(ctx.componentId ?? ''));
        if (!opts.forceRefresh) {
            const recent = recentTokens.get(endpoint);
            if (recent && Date.now() - recent.at < REUSE_MS) return recent.token;
            // Remembered before the first await, so a second caller during
            // boot waits for the same token instead of asking again.
            const prefetched = takePrefetchedToken(ctx.tokenEndpoint);
            if (prefetched !== null) {
                const token = prefetched.then((early) => early ?? mintToken(endpoint, false));
                remember(endpoint, token);
                return token;
            }
        }
        const pending = mintToken(endpoint, opts.forceRefresh === true);
        remember(endpoint, pending);
        return pending;
    }
    return ctx.token;
}

/**
 * How long a token from the endpoint is reused within the page. Loading the
 * Component and reporting the view both need one right after boot; without
 * this each asks the shop. Short, so a token handed out later (an upload
 * minutes after the load) is never close to its expiry.
 */
const REUSE_MS = 30_000;

const recentTokens = new Map<string, { token: Promise<string>; at: number }>();

function remember(key: string, token: Promise<string>): void {
    recentTokens.set(key, { token, at: Date.now() });
    token.catch(() => {
        if (recentTokens.get(key)?.token === token) recentTokens.delete(key);
    });
}

/** For tests: forget reused tokens. */
export function forgetRecentTokens(): void {
    recentTokens.clear();
}

async function mintToken(endpoint: string, force: boolean): Promise<string> {
    const resp = await fetch(force ? appendQueryParam(endpoint, 'force', '1') : endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'accept': 'application/json' },
        credentials: 'omit',
    });
    if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new TokenResolutionError(resp.status, body.slice(0, 200));
    }
    const payload = (await resp.json()) as TokenEndpointResponse;
    if (!payload.token || typeof payload.token !== 'string') {
        throw new TokenResolutionError(502, 'tokenEndpoint: missing or non-string token');
    }
    return payload.token;
}

/**
 * The shop endpoints mint per Component and read its id from the URL —
 * `?id=` or Magento's `/id/{n}/`. Product pages put it there themselves; an
 * embed on any other page (a CMS page) names only the endpoint, so the
 * bundle adds the Component it renders.
 */
function withComponentId(endpoint: string, componentId: string): string {
    if (!/^[1-9][0-9]*$/.test(componentId)) return endpoint;
    if (/[?&]id=/.test(endpoint) || /\/id\/[0-9]+(\/|$|\?)/.test(endpoint)) return endpoint;
    return appendQueryParam(endpoint, 'id', componentId);
}

/**
 * Token requests a shop page started before the bundle loaded, keyed by the
 * exact `tokenEndpoint` URL. A plugin puts a small inline script into the
 * page head, so the token round trip runs while the bundle downloads
 * instead of after it:
 *
 *   (window.ConfigentoTokenPrefetch ||= {})[endpoint] =
 *       fetch(endpoint, { method: 'POST', credentials: 'omit', … })
 *           .then((r) => (r.ok ? r.json() : null)).catch(() => null);
 *
 * Only the token endpoint's URL goes into the (cacheable) HTML, never a
 * token, so full-page caches stay safe.
 */
declare global {
    interface Window {
        ConfigentoTokenPrefetch?: Record<string, Promise<unknown> | undefined>;
    }
}

/**
 * Take a prefetched token request once: null without one, otherwise a
 * promise of the token — or of null when the request failed or brought no
 * token, and the caller asks the endpoint itself. One-shot, so later calls
 * (asset uploads minutes after the load) never get a token that may have aged.
 */
function takePrefetchedToken(endpoint: string): Promise<string | null> | null {
    const store = typeof window !== 'undefined' ? window.ConfigentoTokenPrefetch : undefined;
    const pending = store?.[endpoint];
    if (!store || !pending) return null;
    delete store[endpoint];
    return Promise.resolve(pending).then(
        (payload) => {
            const p = payload as Partial<TokenEndpointResponse> | null;
            return p && typeof p.token === 'string' && p.token !== '' ? p.token : null;
        },
        () => null,
    );
}

function appendQueryParam(url: string, key: string, value: string): string {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
}
